import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { earningRulesAPI } from '@services/index';

const initialState = {
  rules: [],
  selectedRule: null,
  loading: false,
  error: null,
  pagination: {
    count: 0,
    next: null,
    previous: null,
  },
};

export const fetchRules = createAsyncThunk(
  'rules/fetchAll',
  async (params = undefined, { rejectWithValue }) => {
    try {
      const response = await earningRulesAPI.getEarningRules(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rules');
    }
  }
);

export const fetchRuleById = createAsyncThunk(
  'rules/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const rule = await earningRulesAPI.getEarningRule(id);
      return rule;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch rule');
    }
  }
);

export const createRule = createAsyncThunk(
  'rules/create',
  async (data, { rejectWithValue }) => {
    try {
      const rule = await earningRulesAPI.createEarningRule(data);
      return rule;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create rule');
    }
  }
);

export const updateRule = createAsyncThunk(
  'rules/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const rule = await earningRulesAPI.updateEarningRule(id, data);
      return rule;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update rule');
    }
  }
);

export const deleteRule = createAsyncThunk(
  'rules/delete',
  async (id, { rejectWithValue }) => {
    try {
      await earningRulesAPI.deleteEarningRule(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete rule');
    }
  }
);

const rulesSlice = createSlice({
  name: 'rules',
  initialState,
  reducers: {
    clearSelectedRule: (state) => {
      state.selectedRule = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchRules.fulfilled,
        (state, action) => {
          state.loading = false;
          state.rules = action.payload.results;
          state.pagination = {
            count: action.payload.count,
            next: action.payload.next,
            previous: action.payload.previous,
          };
        }
      )
      .addCase(fetchRules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchRuleById.fulfilled, (state, action) => {
        state.selectedRule = action.payload;
      })
      .addCase(createRule.fulfilled, (state, action) => {
        state.rules.unshift(action.payload);
      })
      .addCase(updateRule.fulfilled, (state, action) => {
        const index = state.rules.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.rules[index] = action.payload;
        }
      })
      .addCase(deleteRule.fulfilled, (state, action) => {
        state.rules = state.rules.filter((r) => r.id !== action.payload);
      });
  },
});

export const { clearSelectedRule, clearError } = rulesSlice.actions;
export default rulesSlice.reducer;
